import { useCrmNavigation } from '../hooks/useCrmNavigation';
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Loader2,
  Mail,
  Phone,
  UserCog,
  Clock,
  Search,
  Plus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usePermissions } from '../hooks/usePermissions';
import { leadsAPI, usersAPI } from '../utils/api';
import {
  LeadBrief,
  LeadFilters,
  LeadStato,
  LEAD_STATI,
  LEAD_STATO_LABEL,
} from '../types/lead';
import LeadSourceBadge from '../components/lead/LeadSourceBadge';
import LeadConvertModal from '../components/lead/LeadConvertModal';
import LeadsViewToggle from '../components/lead/LeadsViewToggle';

const COLUMN_STYLE: Record<LeadStato, { header: string; accent: string }> = {
  nuovo: { header: 'bg-blue-50 text-blue-700 border-blue-200', accent: 'border-t-blue-400' },
  contattato: {
    header: 'bg-amber-50 text-amber-700 border-amber-200',
    accent: 'border-t-amber-400',
  },
  qualificato: {
    header: 'bg-purple-50 text-purple-700 border-purple-200',
    accent: 'border-t-purple-400',
  },
  convertito: {
    header: 'bg-green-50 text-green-700 border-green-200',
    accent: 'border-t-green-400',
  },
  scartato: {
    header: 'bg-gray-50 text-gray-600 border-gray-200',
    accent: 'border-t-gray-400',
  },
};

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT');
}

export default function LeadsPipeline() {
  const navigate = useNavigate();
  const { openDetail, openCreate } = useCrmNavigation('/leads');
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('leads:create');
  const canEdit = hasPermission('leads:edit');
  const canConvert = hasPermission('leads:convert');
  const [searchParams, setSearchParams] = useSearchParams();
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<LeadStato | null>(null);
  const [convertTarget, setConvertTarget] = useState<{ id: string; name: string } | null>(null);

  const search = searchParams.get('search') || '';
  const agenteFilter = searchParams.get('agente') || '';

  const filters: LeadFilters = useMemo(
    () => ({
      search: search || undefined,
      agente_id: agenteFilter || undefined,
    }),
    [search, agenteFilter]
  );

  const updateParam = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (!v) next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next);
  };

  const { data: pipelineData, isLoading } = useQuery({
    queryKey: ['leads-pipeline', filters],
    queryFn: async () => {
      const res = await leadsAPI.getPipeline(filters);
      return res.data;
    },
  });

  const { data: agents } = useQuery({
    queryKey: ['users-select-active'],
    queryFn: async () => {
      const res = await usersAPI.getUsers({ limit: 100, is_active: true });
      return res.data.data;
    },
  });

  const moveMutation = useMutation({
    mutationFn: ({ id, stato }: { id: string; stato: LeadStato }) =>
      leadsAPI.updateLead(id, { stato }),
    onMutate: async ({ id, stato }) => {
      await queryClient.cancelQueries({ queryKey: ['leads-pipeline'] });
      const previous = queryClient.getQueryData<typeof pipelineData>(['leads-pipeline', filters]);
      if (previous) {
        const next = {
          ...previous,
          data: { ...previous.data } as Record<LeadStato, LeadBrief[]>,
        };
        let moved: LeadBrief | undefined;
        for (const col of LEAD_STATI) {
          const idx = next.data[col]?.findIndex((l) => l.id === id) ?? -1;
          if (idx >= 0) {
            [moved] = next.data[col].splice(idx, 1);
            break;
          }
        }
        if (moved) {
          moved = { ...moved, stato };
          next.data[stato] = [moved, ...(next.data[stato] || [])];
        }
        queryClient.setQueryData(['leads-pipeline', filters], next);
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['leads-pipeline', filters], ctx.previous);
      }
      toast.error('Impossibile spostare il lead');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['leads-pipeline'] });
      toast.success('Stato aggiornato');
    },
  });

  const onDragStart = (id: string) => setDraggingId(id);
  const onDragEnd = () => {
    setDraggingId(null);
    setDragOverCol(null);
  };

  const onDrop = (target: LeadStato, e: React.DragEvent) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || draggingId;
    setDragOverCol(null);
    setDraggingId(null);
    if (!id) return;

    // Find current stato
    const current = pipelineData?.data;
    if (!current) return;
    let fromStato: LeadStato | null = null;
    let lead: LeadBrief | undefined;
    for (const col of LEAD_STATI) {
      const found = current[col]?.find((l) => l.id === id);
      if (found) {
        fromStato = col;
        lead = found;
        break;
      }
    }
    if (!lead || fromStato === target) return;

    // If dropping on "convertito", open the convert modal instead of moving directly
    if (target === 'convertito') {
      if (!canConvert) return;
      setConvertTarget({ id: lead.id, name: lead.denominazione_completa });
      return;
    }
    if (!canEdit) return;
    moveMutation.mutate({ id, stato: target });
  };

  const columns = pipelineData?.data || ({} as Record<LeadStato, LeadBrief[]>);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pipeline Lead</h1>
          <p className="text-gray-600">Sposta i lead tra le colonne per aggiornare lo stato</p>
        </div>
        <div className="flex items-center gap-2">
          <LeadsViewToggle current="pipeline" />
          {canCreate && (
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <Plus className="h-4 w-4" />
              Nuovo Lead
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-3">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca nome, email, telefono..."
            value={search}
            onChange={(e) => updateParam({ search: e.target.value })}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
        <select
          value={agenteFilter}
          onChange={(e) => updateParam({ agente: e.target.value })}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
        >
          <option value="">Tutti gli agenti</option>
          {agents?.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name || u.email}
            </option>
          ))}
        </select>
      </div>

      {/* Kanban */}
      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {LEAD_STATI.map((stato) => {
            const items = columns[stato] || [];
            const style = COLUMN_STYLE[stato];
            const isOver = dragOverCol === stato;
            return (
              <div
                key={stato}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverCol(stato);
                }}
                onDragLeave={() => setDragOverCol((cur) => (cur === stato ? null : cur))}
                onDrop={(e) => onDrop(stato, e)}
                className={`bg-gray-50 rounded-lg border-t-4 ${style.accent} ${
                  isOver ? 'ring-2 ring-blue-400 bg-blue-50/40' : ''
                } transition-colors flex flex-col min-h-[200px]`}
              >
                <div className={`px-3 py-2 border-b ${style.header} rounded-t-md`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">{LEAD_STATO_LABEL[stato]}</span>
                    <span className="text-xs px-2 py-0.5 bg-white/60 rounded-full">
                      {items.length}
                    </span>
                  </div>
                </div>
                <div className="p-2 space-y-2 flex-1">
                  {items.length === 0 ? (
                    <div className="text-center text-xs text-gray-400 py-6">Nessun lead</div>
                  ) : (
                    items.map((lead) => (
                      <KanbanCard
                        key={lead.id}
                        lead={lead}
                        canDrag={canEdit || canConvert}
                        isDragging={draggingId === lead.id}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        onClick={() => openDetail(lead.id)}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}



      {convertTarget && (
        <LeadConvertModal
          isOpen={!!convertTarget}
          leadId={convertTarget.id}
          leadName={convertTarget.name}
          onClose={() => setConvertTarget(null)}
          onConverted={(clienteId) => {
            setConvertTarget(null);
            queryClient.invalidateQueries({ queryKey: ['leads-pipeline'] });
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            navigate(`/clients/${clienteId}`);
          }}
        />
      )}
    </div>
  );
}

function KanbanCard({
  lead,
  canDrag,
  isDragging,
  onDragStart,
  onDragEnd,
  onClick,
}: {
  lead: LeadBrief;
  canDrag: boolean;
  isDragging: boolean;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onClick: () => void;
}) {
  return (
    <div
      draggable={canDrag}
      onDragStart={(e) => {
        if (!canDrag) return;
        e.dataTransfer.setData('text/plain', lead.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(lead.id);
      }}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={`bg-white rounded-md border border-gray-200 p-3 shadow-sm hover:shadow ${
        canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
      } ${isDragging ? 'opacity-50' : ''}`}
    >
      <div className="text-sm font-medium text-gray-900 truncate">
        {lead.denominazione_completa || '—'}
      </div>
      <div className="mt-1 flex items-center gap-2 flex-wrap">
        <LeadSourceBadge source={lead.source} />
        {lead.score > 0 && (
          <span className="text-xs font-semibold text-orange-600">Score {lead.score}</span>
        )}
      </div>
      <div className="mt-2 space-y-1 text-xs text-gray-600">
        {lead.email && (
          <div className="flex items-center gap-1 truncate">
            <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <span className="truncate">{lead.email}</span>
          </div>
        )}
        {lead.telefono && (
          <div className="flex items-center gap-1">
            <Phone className="h-3.5 w-3.5 text-gray-400" />
            {lead.telefono}
          </div>
        )}
        {lead.agente_nome && (
          <div className="flex items-center gap-1">
            <UserCog className="h-3.5 w-3.5 text-gray-400" />
            {lead.agente_nome}
          </div>
        )}
        <div className="flex items-center gap-1 text-gray-500">
          <Clock className="h-3.5 w-3.5 text-gray-400" />
          {formatDate(lead.ultimo_contatto_at)}
        </div>
      </div>
    </div>
  );
}
